import { Component, inject } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  Validators
} from '@angular/forms';
import { ZipCodeService } from '@artemis-software/wr-api';
import { debounceTime, filter, switchMap } from 'rxjs';
import { AbstractFormControl } from '@shared/form-controls/abstract-form-control';

type AutomaticAddress = Partial<{
  city: string;
  state: string;
  zipCode: string;
}
>
@Component({
  standalone: false,
  selector: 'wr-automatic-address',
  templateUrl: './automatic-address.component.html',
  styleUrls: ['./automatic-address.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: AutomaticAddressComponent,
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: AutomaticAddressComponent,
      multi: true,
    }
  ],
})
export class AutomaticAddressComponent extends AbstractFormControl<AutomaticAddress> implements Validator{
  private readonly zipCodeService = inject(ZipCodeService);

  readonly internalForm = new FormGroup({
    zipCode: new FormControl('', [Validators.required]),
    city: new FormControl('', [Validators.required]),
    state: new FormControl('' , [Validators.required]),
  });

  constructor() {
    super();
    this.setupAutoFillListener();
    this.setupValueSync();
  }

  private setupAutoFillListener() {
    this.internalForm.get('zipCode')!.valueChanges.pipe(
      debounceTime(300),
      filter(zip => !!zip && zip.length >= 4),
      switchMap(zip => this.zipCodeService.findByZipCode(zip!)),
      filter(address => !!address),
    ).subscribe(address => {

      this.internalForm.patchValue({
        city: address.city,
        state: address.state
      });
    });
  }

  private setupValueSync() {
    this.internalForm.valueChanges.subscribe((val) => {
      this.value = {
        city: val.city ?? undefined,
        state: val.state ?? undefined,
        zipCode: val.zipCode ?? undefined
      };
    });
  }

  override writeValue(value: AutomaticAddress): void {
    if (value) {
      this.internalForm.patchValue(value, { emitEvent: false });
      super.writeValue({
        city: value.city ?? undefined,
        state: value.state ?? undefined,
        zipCode: value.zipCode ?? undefined
      });
    } else {
      this.internalForm.reset({}, { emitEvent: false });
      super.writeValue(undefined);
    }
  }

  override setDisabledState(isDisabled: boolean): void {
    if (!this.internalForm) {
      return;
    }

    if (isDisabled) {
      this.internalForm.disable({ emitEvent: false });
    } else {
      this.internalForm.enable({ emitEvent: false });
    }
  }

  validate(_control: AbstractControl): ValidationErrors | null {
    if (this.internalForm.invalid) {
      return { addressIncomplete: true };
    }
    return null;
  }

  override markAsTouched() {
    super.markAsTouched();
    this.internalForm.markAllAsTouched();
  }

  hasError(controlName: string): boolean {
    const control = this.internalForm.get(controlName);
    return !!(control?.invalid && (control.dirty || control.touched));
  }
}
